'use client'

import { useCallback, useEffect, useRef } from 'react'
import { getTurnstileSiteKey, loadTurnstileScript } from '@/lib/turnstile'

let turnstileContainerCount = 0

type PendingChallenge = {
  resolve: (token: string) => void
  reject: (error: Error) => void
  timeoutId: ReturnType<typeof setTimeout>
}

export const useTurnstile = (action?: string) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const containerIdRef = useRef<string | null>(null)
  const pendingChallengeRef = useRef<PendingChallenge | null>(null)
  const widgetIdRef = useRef<string | null>(null)

  const rejectPendingChallenge = useCallback((message: string) => {
    const pendingChallenge = pendingChallengeRef.current
    if (!pendingChallenge) return

    clearTimeout(pendingChallenge.timeoutId)
    pendingChallengeRef.current = null
    pendingChallenge.reject(new Error(message))
  }, [])

  const getContainer = useCallback(() => {
    if (containerRef.current) return containerRef.current

    const container = document.createElement('div')
    if (!containerIdRef.current) {
      turnstileContainerCount += 1
      containerIdRef.current = `turnstile-${turnstileContainerCount}`
    }

    container.id = containerIdRef.current
    container.style.position = 'fixed'
    container.style.right = '24px'
    container.style.bottom = '24px'
    container.style.zIndex = '2147483647'
    document.body.appendChild(container)

    containerRef.current = container
    return container
  }, [])

  const getWidgetId = useCallback(async () => {
    const siteKey = getTurnstileSiteKey()
    await loadTurnstileScript()

    if (!window.turnstile) {
      throw new Error('Turnstile is not available')
    }

    if (widgetIdRef.current) return widgetIdRef.current

    const container = getContainer()
    const widgetId = window.turnstile.render(`#${container.id}`, {
      sitekey: siteKey,
      execution: 'execute',
      appearance: 'interaction-only',
      action,
      callback: token => {
        const pendingChallenge = pendingChallengeRef.current
        if (!pendingChallenge) return

        clearTimeout(pendingChallenge.timeoutId)
        pendingChallengeRef.current = null
        pendingChallenge.resolve(token)
      },
      'error-callback': errorCode => {
        rejectPendingChallenge(`Turnstile failed: ${errorCode}`)
      },
      'expired-callback': () => {
        rejectPendingChallenge('Turnstile token expired')
      },
      'timeout-callback': () => {
        rejectPendingChallenge('Turnstile challenge timed out')
      },
    })

    widgetIdRef.current = widgetId
    return widgetId
  }, [action, getContainer, rejectPendingChallenge])

  const executeTurnstile = useCallback(async () => {
    const widgetId = await getWidgetId()

    if (!window.turnstile) {
      throw new Error('Turnstile is not available')
    }

    rejectPendingChallenge('Turnstile challenge was replaced')

    return new Promise<string>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        pendingChallengeRef.current = null
        reject(new Error('Turnstile challenge timed out'))
      }, 60000)

      pendingChallengeRef.current = { resolve, reject, timeoutId }

      try {
        window.turnstile?.reset(widgetId)
        window.turnstile?.execute(widgetId)
      } catch (error) {
        clearTimeout(timeoutId)
        pendingChallengeRef.current = null
        reject(error instanceof Error ? error : new Error('Turnstile failed'))
      }
    })
  }, [getWidgetId, rejectPendingChallenge])

  useEffect(() => {
    return () => {
      rejectPendingChallenge('Turnstile challenge was cancelled')

      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
      }

      containerRef.current?.remove()
      widgetIdRef.current = null
      containerRef.current = null
    }
  }, [rejectPendingChallenge])

  return { executeTurnstile }
}
